import { db } from "helpers/DBHelper";
import * as SalesHelper from "helpers/SalesHelper";
import { COLLECTIONS, SALE_STATUS, KOT_STATUS, KOT_TITLES, INVOICE_TARGET_TYPE } from "const";
import * as TableAreaHelper from "helpers/TableAreaHelper";
import KotManager from "helpers/KotManager";
import * as TerminalHelper from "helpers/TermialHelper";
import ShopsHelper from "helpers/ShopsHelper";
import * as ProductHelper from "helpers/ProductHelper";

export const handleRequest = async (path, method, body, queryParams, salesPerson) => {
  method = method.toUpperCase();

  // Carts list endpoint
  if (path === "/carts") {
    return handleSale(method, body, queryParams, salesPerson);
  }

  // Carts details endpoint
  const cartDetailsMatch = path.match(/^\/carts\/([0-9]+)$/);
  if (cartDetailsMatch) {
    const id = cartDetailsMatch[1];
    return handleSaleDetails(method, id, body, salesPerson);
  }

  // Carts items endpoint
  const itemsToCartMatch = path.match(/^\/carts\/([0-9]+)\/items$/);
  if (itemsToCartMatch) {
    const id = itemsToCartMatch[1];
    return handleItemsToCart(method, Number(id), body, queryParams);
  }

  // Carts Split endpoint
  const splitCartMatch = path.match(/^\/carts\/([0-9]+)\/split$/);
  if (splitCartMatch) {
    const id = splitCartMatch[1];
    return handleCartSplit(method, Number(id), body);
  }

  // Carts Merge endpoint
  if (path === "/carts/merge") {
    return handleCartMerge(method, body);
  }

  // Carts change-table endpoint
  const changeTableMatch = path.match(/^\/carts\/([0-9]+)\/change-table$/);
  if (changeTableMatch) {
    const id = changeTableMatch[1];
    return handleChangeCartTable(method, Number(id), body);
  }

  // Carts re-print endpoint
  const reprintMatch = path.match(/^\/carts\/([0-9]+)\/re-print$/);
  if (reprintMatch) {
    const id = reprintMatch[1];
    return handleKotReprint(method, Number(id));
  }

  // Carts item status endpoint
  const cartItemStatusMatch = path.match(/^\/carts\/([0-9]+)\/items\/([0-9]+)$/);
  if (cartItemStatusMatch) {
    const cartId = cartItemStatusMatch[1];
    const itemId = cartItemStatusMatch[2];
    return handleCartItemStatusUpdate(method, Number(cartId), Number(itemId), body);
  }

  // Create order
  if (path === "/orders") {
    return handleCreateOrder(method, body);
  }

  return { message: "API not found", status: 404 };
};

async function handleCartItemStatusUpdate(method, cartId, itemId, body) {
  if (method !== "PATCH" && method !== "POST") return { status: 405, message: "Method not allowed" };

  try {
    const { status, option, allItems } = body;
    if (!status) return { status: 400, message: "Status required" };

    await SalesHelper.updateCartItemStatus(Number(cartId), Number(itemId), status, option, allItems);
    return { status: 200, message: "Success" };
  } catch (error) {
    return { status: 500, message: error.message || "Failed to update item status" };
  }
}

async function resolveInvoiceTarget(invoiceTargetType, invoiceTargetValue, sale) {
  if (invoiceTargetType === INVOICE_TARGET_TYPE.COUNTER) {
    let areaIp = null;
    if (sale?.table?.area?.id) {
      const areas = await TableAreaHelper.getAreasFromDB();
      const area = areas.find((a) => a.id === sale.table.area.id);
      areaIp = area?.invoicePrinterIp || area?.invoice_printer_ip;
    }

    if (areaIp) {
      return {
        finalInvoiceTargetType: INVOICE_TARGET_TYPE.IP,
        finalInvoiceTargetValue: areaIp,
      };
    }

    return {
      finalInvoiceTargetType: INVOICE_TARGET_TYPE.COUNTER,
      finalInvoiceTargetValue: null,
    };
  }

  return {
    finalInvoiceTargetType: invoiceTargetType,
    finalInvoiceTargetValue: invoiceTargetValue,
  };
}

async function handleCreateOrder(method, body) {
  if (method !== "POST") return { status: 405, message: "Method not allowed" };

  try {
    const { cartId, invoiceTargetType, invoiceTargetValue, tenders = [], change } = body;

    const sale = await SalesHelper.getSaleById(cartId);

    if (sale?.status === SALE_STATUS.PAID || sale?.status === SALE_STATUS.PRINTED) {
      return { status: 400, message: "Bill already printed" };
    }

    if (!sale) return { status: 404, message: "Sale not found" };

    const { finalInvoiceTargetType, finalInvoiceTargetValue } = await resolveInvoiceTarget(
      invoiceTargetType,
      invoiceTargetValue,
      sale,
    );

    let finalTenders = tenders;
    const terminalSettings = TerminalHelper.getTerminalSettings();

    // Auto-payment logic for Captain Invoice
    if (terminalSettings.autoReceiveFullAmount && (!tenders || tenders.length === 0)) {
      const paymentModes = ShopsHelper.getPaymentModes();
      const defaultMode = paymentModes && paymentModes.length > 0 ? paymentModes[0] : null;
      if (defaultMode) {
        finalTenders = [
          {
            id: defaultMode.id,
            name: defaultMode.name,
            amount: sale.payable,
          },
        ];
      }
    }

    const applyPaymentToSale = await SalesHelper.applyPaymentToSale(sale, finalTenders, null, change);

    const updatedSale = await SalesHelper.saveAndProcess(applyPaymentToSale, {
      invoiceTargetType: finalInvoiceTargetType,
      invoiceTargetValue: finalInvoiceTargetValue,
    });

    SalesHelper.notifySalesUpdate(updatedSale);

    return { status: 200, message: "Success" };
  } catch (error) {
    return { status: 500, message: error.message || "Failed to create order" };
  }
}

function handleSale(method, body, queryParams, salesPerson) {
  switch (method) {
    case "GET":
      return getActiveSales(queryParams);
    case "POST":
      // Existing create sale logic
      return createSale(body, salesPerson, queryParams);
    default:
      return { status: 405, message: `Method ${method} not allowed` };
  }
}

async function handleSaleDetails(method, id, body, salesPerson) {
  id = Number(id);
  if (method === "GET") {
    const sale = await SalesHelper.getSaleById(id);
    if (!sale) return { status: 404, message: "Not found" };

    const responseData = SalesHelper.formatSaleForAndroidResponse({ ...sale, waiter: salesPerson });
    return { status: 200, message: "Cart details", data: responseData };
  }
  if (method === "PATCH") {
    // Reuse existing updateSale logic but adapted for ID from path
    if (body?.tokenNumber !== undefined) {
      const updatedSale = await SalesHelper.updateSaleTokenNumber(id, body.tokenNumber);

      return {
        status: 200,
        message: "Token number updated successfully",
        data: updatedSale,
      };
    }
    if (body?.status !== undefined) {
      await SalesHelper.updateSaleKotStatus(id, body.status, { notify: true });
      const updatedSale = await SalesHelper.getSaleById(id);

      if (!updatedSale) {
        return { status: 404, message: "Sale not found after update" };
      }

      return {
        status: 200,
        message: "Order status updated successfully",
        data: SalesHelper.formatSaleForAndroidResponse({ ...updatedSale, waiter: salesPerson }),
      };
    }
    return updateSale({ ...body, salesId: id }, salesPerson, { notify: true });
  }
  return { status: 405, message: `Method ${method} not allowed` };
}

async function handleItemsToCart(method, id, body, queryParams) {
  if (method === "POST") {
    return handleAddItemsToCart(id, body, queryParams);
  }
  if (method === "PUT") {
    return handleEditItemsToCart(id, body, queryParams);
  }
  return { status: 405, message: `Method ${method} not allowed` };
}

async function handleCartSplit(method, id, body) {
  if (method !== "POST") return { status: 405, message: "Method not allowed" };

  try {
    const itemsToSplit = body.items || [];
    if (itemsToSplit.length === 0) {
      return { status: 400, message: "Cart items should not be empty!" };
    }

    await SalesHelper.splitSale(id, itemsToSplit, { notify: true });
    return { status: 200, message: "Success" };
  } catch (error) {
    return { status: 500, message: error.message || "Failed to split cart" };
  }
}

async function handleCartMerge(method, body) {
  if (method !== "POST") return { status: 405, message: "Method not allowed" };

  try {
    const cartIds = body.cartIds || [];
    if (cartIds.length < 2) {
      return { status: 400, message: "At least two valid orders are required to merge" };
    }

    await SalesHelper.mergeSalesId(cartIds, { notify: true });
    return { status: 200, message: "Success" };
  } catch (error) {
    return { status: 500, message: error.message || "Failed to merge order" };
  }
}

async function handleChangeCartTable(method, id, body) {
  if (method !== "POST") return { status: 405, message: "Method not allowed" };

  try {
    const { tableId, chairId } = body;
    const oldSale = await SalesHelper.getSaleById(id);
    const oldTable = oldSale?.table;
    const updatedSale = await SalesHelper.changeTable(id, tableId, chairId, { notify: true });

    try {
      const categories = await ProductHelper.getCategoriesFromDB();
      const oldChairSuffix = oldTable?.chair?.name ? ` > ${oldTable.chair.name}` : "";
      const newChairSuffix = updatedSale?.table?.chair?.name ? ` > ${updatedSale.table.chair.name}` : "";
      const oldTableName = `${oldTable?.area?.name || "-"} > ${oldTable?.name || "-"}${oldChairSuffix}`;
      const newTableName = `${updatedSale?.table?.area?.name || "-"} > ${updatedSale?.table?.name || "-"}${newChairSuffix}`;
      const tableNotes = [`Table changed:\n${oldTableName}  -->  ${newTableName}`];
      const terminalSettings = TerminalHelper.getTerminalSettings();
      const showItems = !!terminalSettings.showItemsOnKotWhenTableChange;

      KotManager.printKotSafe({
        sale: {
          ...updatedSale,
          items: updatedSale.items || [],
        },
        fallbackPrintFunction: () => {},
        categories,
        titles: [KOT_TITLES.TABLE_CHANGED],
        notes: tableNotes,
        skipConsolidatedKOT: !terminalSettings.consolidatedKOT,
        showItems,
      });
    } catch (printError) {
      console.error("Failed to print KOT on table change: ", printError);
    }

    return { status: 200, message: "Success" };
  } catch (error) {
    return { status: 500, message: error.message || "Failed to change table" };
  }
}

async function handleKotReprint(method, id) {
  if (method !== "GET" && method !== "POST") {
    return { status: 405, message: "Method not allowed" };
  }

  const sale = await SalesHelper.getSaleById(id);
  if (!sale) {
    return { status: 404, message: "Not found" };
  }

  try {
    const categories = await ProductHelper.getCategoriesFromDB();
    KotManager.printKotSafe({
      sale: {
        ...sale,
        items: sale.items || [],
      },
      fallbackPrintFunction: () => {},
      categories,
      titles: [KOT_TITLES.REPRINT],
    });
    return { status: 200, message: "Success" };
  } catch (error) {
    return { status: 500, message: error.message || "Failed to reprint KOT" };
  }
}

async function handleAddItemsToCart(id, body, queryParams) {
  const { items: inputItems = [], orderType = null } = body || {};

  const mappedItems = inputItems.map((item) => ({
    id: item.itemId,
    qty: item.quantity,
    note: item.note || "",
    option: item.option,
  }));

  const { updatedSale } = await SalesHelper.addItemsToExistingSale(id, mappedItems, {
    notify: true,
    orderType,
  });

  // Printing KOT
  KotManager.printRemainingKotSafe({
    sale: updatedSale,
    kotPrintRequired: queryParams?.kotPrintRequired !== "false",
  });

  return { status: 200, message: "Success" };
}

async function handleEditItemsToCart(id, body, queryParams) {
  const { items: inputItems = [], orderType = null } = body || {};

  const mappedItems = inputItems.map((item) => ({
    id: item.itemId,
    qty: item.quantity,
    note: item.note || "",
    option: item.option,
  }));

  const { updatedSale } = await SalesHelper.editSaleItems(id, mappedItems, {
    notify: true,
    orderType,
  });

  // Printing KOT
  KotManager.printRemainingKotSafe({
    sale: updatedSale,
    kotPrintRequired: queryParams?.kotPrintRequired !== "false",
  });

  return { status: 200, message: "Success" };
}

export const getActiveSales = async (queryParams = {}) => {
  const targetOrderStatuses = queryParams.status
    ? queryParams.status.split(",").map((s) => s.trim().toUpperCase())
    : null;

  const data = await db.filter(COLLECTIONS.SALES.name, (sale) => {
    if (sale.editing_sale_local_id) {
      return false;
    }

    // 1. If KDS specifies fulfillment statuses, filter ONLY by those
    if (targetOrderStatuses) {
      const currentOrderStatus = (sale.kot_status || KOT_STATUS.PENDING).toUpperCase();
      return targetOrderStatuses.includes(currentOrderStatus);
    }

    // 2. If no fulfillment status is specified, fallback to active payment status
    return sale.status === SALE_STATUS.DRAFT;
  });

  const formattedSales = data.map((sale) => {
    const formatted = SalesHelper.formatSaleForAndroidResponse(sale);
    if (targetOrderStatuses) {
      formatted.token_no = sale.token_no || sale.local_id;
    }
    return formatted;
  });
  return {
    data: formattedSales,
    message: "Carts list",
    status: 200,
  };
};

export const getSales = async () => {
  const data = await db.getAll(COLLECTIONS.SALES.name);

  return {
    data: data,
    message: "Sale list",
    status: 200,
  };
};

async function createSale(body, salesperson, params) {
  if (!salesperson) {
    return { message: "Unauthorized", status: 401 };
  }

  const {
    tableId = null,
    chairId = null,
    items: inputItems = [],
    customer = null,
    tokenNumber = null,
    orderType = null,
  } = body || {};

  const mappedItems = inputItems.map((item) => ({
    id: item.itemId,
    qty: item.quantity,
    note: item.note || "",
    option: item.option,
  }));

  const saleData = {
    table: tableId ? { id: tableId, chairId } : undefined,
    items: mappedItems,
    customer: customer,
    token_no: tokenNumber,
    waiter: salesperson,
    order_type: orderType,
  };

  const sale = await SalesHelper.createNewSale(saleData, { notify: true });

  const responseData = SalesHelper.formatSaleForAndroidResponse(sale);

  // Printing KOT async
  KotManager.printRemainingKotSafe({ sale, kotPrintRequired: params?.kotPrintRequired !== "false" });

  return {
    data: responseData,
    message: "Cart created",
    status: 200,
  };
}

async function updateSale(body, salesPerson, options = {}) {
  if (!salesPerson) {
    return { message: "Unauthorized", status: 401 };
  }

  const { salesId = null, items: inputItems = [], customer = null, tokenNumber = null } = body || {};

  if (!salesId) {
    return { message: "Sales ID (bill number) required", status: 400 };
  }

  if (tokenNumber !== undefined && tokenNumber !== null) {
    await SalesHelper.updateSaleTokenNumber(salesId, tokenNumber, options);
  }

  if (customer !== undefined && customer !== null) {
    await SalesHelper.updateSaleCustomer(salesId, customer, options);
  }

  const { updatedSale } = await SalesHelper.addItemsToExistingSale(salesId, inputItems, options);

  if (inputItems && inputItems.length > 0) {
    KotManager.printRemainingKotSafe({
      sale: updatedSale,
      kotPrintRequired: options?.kotPrintRequired !== false,
    });
  }

  const responseData = SalesHelper.formatSaleForAndroidResponse({ ...updatedSale, waiter: salesPerson });

  return {
    data: responseData,
    message: "Cart updated",
    status: 200,
  };
}
